import { useState, forwardRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Crown, Zap, Tag, AlertTriangle,
  ChevronDown, ShieldCheck, Eye, Bookmark, Clock,
  Ban, ShoppingCart, ExternalLink, Search, Sparkles,
} from "lucide-react";
import PurchaseFeedback from "./PurchaseFeedback";
import CoverageBadge from "./CoverageBadge";
import BuyFlowModal from "./BuyFlowModal";
import UpgradeModal from "./UpgradeModal";
import type { DecisionOption, CoverageState, ConfidenceLevel } from "@/lib/decisionEngine";
import { getRepeatBuyCycle } from "@/lib/categoryTaxonomy";
import { trackOutcome } from "@/lib/outcomeTracker";
import { captureIntent } from "@/lib/intentCapture";
import { usePlan, canUseFeatureForPlan } from "@/hooks/usePlan";
import { addToWatchlist } from "@/lib/watchlistStore";
import { loadMemory } from "@/lib/userMemory";
import { analyzeBehavior } from "@/lib/behaviorEngine";
import { useAuth } from "@/contexts/AuthContext";
import { saveToCloudWatchlistFromCard, logEvent, saveReorderItem } from "@/lib/cloudServices";
import { toast } from "@/hooks/use-toast";
import { trackSave, trackClick } from "@/lib/behaviorTracking";
import { resolveCategory } from "@/lib/categoryTaxonomy";

const labelConfig: Record<
  DecisionOption["label"],
  { icon: typeof Crown; gradient: string; badgeClass: string; verdictClass: string }
> = {
  Recommended: {
    icon: Crown, gradient: "from-primary/8 to-card",
    badgeClass: "bg-primary/12 text-primary border-primary/15", verdictClass: "text-primary",
  },
  "Best Value": {
    icon: Tag, gradient: "from-accent/6 to-card",
    badgeClass: "bg-accent/12 text-accent border-accent/15", verdictClass: "text-accent",
  },
  "Fastest Delivery": {
    icon: Zap, gradient: "from-premium-accent/6 to-card",
    badgeClass: "bg-premium-accent/12 text-premium-accent border-premium-accent/15", verdictClass: "text-premium-accent",
  },
  "Premium Pick": {
    icon: Crown, gradient: "from-premium-accent/6 to-card",
    badgeClass: "bg-premium-accent/12 text-premium-accent border-premium-accent/15", verdictClass: "text-premium-accent",
  },
};

const confidenceConfig: Record<ConfidenceLevel, { text: string; className: string }> = {
  high: { text: "High confidence", className: "text-score-high" },
  medium: { text: "Moderate confidence", className: "text-score-mid" },
  low: { text: "Limited confidence", className: "text-muted-foreground" },
  insufficient: { text: "Insufficient data", className: "text-score-low" },
};

function getRegretLine(option: DecisionOption): string | null {
  const { scores } = option;
  if (scores.delivery < 80) return "Avoid this if next-day delivery is non-negotiable.";
  if (scores.price < 50) return "Avoid this if lowest price matters more than route quality.";
  if (scores.quality < 75) return "Avoid this if premium build quality is a requirement.";
  if (scores.trust < 80) return "Avoid this if you only want direct-brand routes.";
  return null;
}

// Link type display
function getLinkTypeLabel(linkType?: string): { label: string; icon: typeof ExternalLink; className: string } {
  switch (linkType) {
    case "product-page":
      return { label: "Product page", icon: ExternalLink, className: "text-score-high" };
    case "search-page":
      return { label: "Retailer search", icon: Search, className: "text-score-mid" };
    default:
      return { label: "Visit retailer", icon: ExternalLink, className: "text-muted-foreground" };
  }
}

// Score context descriptions
const SCORE_CONTEXT: Record<string, (v: number) => string> = {
  Price: (v) => v >= 85 ? "Strong value for money" : v >= 65 ? "Fair pricing relative to market" : "Higher than average",
  Delivery: (v) => v >= 85 ? "Fast, reliable shipping" : v >= 65 ? "Standard delivery timeline" : "Slower or uncertain delivery",
  Trust: (v) => v >= 85 ? "Strong retailer reliability" : v >= 65 ? "Adequate sourcing confidence" : "Limited verification",
  Quality: (v) => v >= 85 ? "Excellent product fit" : v >= 65 ? "Solid product match" : "Less evidence than alternatives",
};

function ScoreBar({ label, value, isEstimated }: { label: string; value: number; isEstimated?: boolean }) {
  const color = value >= 85 ? "bg-score-high" : value >= 65 ? "bg-score-mid" : "bg-score-low";
  const context = SCORE_CONTEXT[label]?.(value) || "";
  return (
    <div className="flex flex-col gap-0.5">
      <div className="flex items-center gap-2.5">
        <span className="text-[10px] text-muted-foreground font-body w-12 shrink-0">{label}</span>
        <div className={`flex-1 h-[3px] bg-muted/50 rounded-full overflow-hidden ${isEstimated ? "opacity-60" : ""}`}>
          <motion.div initial={{ width: 0 }} animate={{ width: `${value}%` }} transition={{ duration: 0.8, ease: "easeOut", delay: 0.3 }} className={`h-full rounded-full ${color} ${isEstimated ? "opacity-70" : ""}`} />
        </div>
        <span className={`text-[10px] font-body w-7 text-right ${isEstimated ? "text-muted-foreground/50" : "text-muted-foreground/70"}`}>
          {isEstimated ? `~${value}` : value}
        </span>
      </div>
      {context && (
        <span className="text-[9px] text-muted-foreground/50 font-body pl-[3.5rem]">{context}</span>
      )}
    </div>
  );
}

function VerificationBadge({ verification }: { verification?: string }) {
  if (!verification) return null;

  if (verification === "ai-estimated") {
    return (
      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-body border border-border/30 text-muted-foreground bg-muted/10">
        AI estimate
      </span>
    );
  }

  const config = {
    "verified": { text: "Price verified", className: "text-score-high bg-score-high/10 border-score-high/20" },
    "partially-verified": { text: "Partially verified", className: "text-score-mid bg-score-mid/10 border-score-mid/20" },
  }[verification] || null;

  if (!config) return null;

  return (
    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-body border ${config.className}`}>
      {verification === "verified" && <ShieldCheck className="w-2.5 h-2.5" />}
      {config.text}
    </span>
  );
}

const VerifiedOffersSection = forwardRef<HTMLDivElement, { option: DecisionOption }>(function VerifiedOffersSection({ option }, ref) {
  const offers = option.verifiedOffers;
  if (!offers || offers.length === 0) return null;

  return (
    <div ref={ref} className="space-y-1">
      <p className="text-[10px] text-muted-foreground font-body font-medium">Verified offers</p>
      {offers.slice(0, 3).map((offer, i) => {
        const lt = getLinkTypeLabel(offer.linkType);
        const LinkIcon = lt.icon;
        return (
          <a
            key={i}
            href={offer.url}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-between rounded-md border border-border/30 bg-muted/10 px-2.5 py-1.5 hover:border-primary/25 transition-colors"
          >
            <div className="flex items-center gap-2">
              <ShieldCheck className={`w-3 h-3 ${offer.trustScore >= 85 ? "text-score-high" : offer.trustScore >= 70 ? "text-score-mid" : "text-score-low"}`} />
              <div>
                <p className="text-[11px] font-body text-foreground">{offer.retailer}</p>
                <div className="flex items-center gap-1.5">
                  <p className="text-[9px] text-muted-foreground font-body">{offer.delivery} · {offer.isDomestic ? "Local" : "International"}</p>
                  <span className={`text-[8px] font-body ${lt.className} flex items-center gap-0.5`}>
                    <LinkIcon className="w-2 h-2" />
                    {lt.label}
                  </span>
                </div>
              </div>
            </div>
            <div className="text-right">
              <p className="text-[11px] font-body font-semibold text-foreground">{offer.price}</p>
              {offer.landedCost && offer.landedCost.dutyEstimate > 0 && (
                <p className="text-[9px] text-muted-foreground font-body">+duty</p>
              )}
            </div>
          </a>
        );
      })}
    </div>
  );
});

function AuditSection({ option, plan }: { option: DecisionOption; plan: string }) {
  const [open, setOpen] = useState(false);
  const hasRealSources = option.audit.sourcesAnalyzed > 0 && option.audit.offersCompared > 0;
  const isFree = plan === "free" || plan === "guest";
  
  const auditLabel = hasRealSources
    ? `Checked ${option.audit.offersCompared} offers across ${option.audit.sourcesAnalyzed} sources`
    : "AI-analyzed from current market knowledge";

  return (
    <div className="border-t border-border/30 mt-1">
      <button onClick={() => setOpen(!open)} className="flex items-center justify-between w-full py-2 text-[10px] text-muted-foreground font-body hover:text-foreground transition-colors">
        <span className="flex items-center gap-1.5">
          <Eye className="w-3 h-3" />
          {auditLabel}
        </span>
        <motion.div animate={{ rotate: open ? 180 : 0 }} transition={{ duration: 0.2 }}><ChevronDown className="w-3 h-3" /></motion.div>
      </button>
      <AnimatePresence>
        {open && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.25 }} className="overflow-hidden">
            <div className="pb-3 space-y-2.5">
              {hasRealSources && (
                <div className={`grid gap-2 ${option.audit.optionsFiltered > 0 ? "grid-cols-3" : "grid-cols-2"}`}>
                  <div className="rounded-lg bg-muted/20 px-2 py-1.5 text-center">
                    <p className="text-sm font-display font-bold text-foreground">{option.audit.offersCompared}</p>
                    <p className="text-[9px] text-muted-foreground font-body">Checked</p>
                  </div>
                  {option.audit.optionsFiltered > 0 && (
                    <div className="rounded-lg bg-muted/20 px-2 py-1.5 text-center">
                      <p className="text-sm font-display font-bold text-foreground">{option.audit.optionsFiltered}</p>
                      <p className="text-[9px] text-muted-foreground font-body">Filtered</p>
                    </div>
                  )}
                  {option.audit.dataConsistency !== "low" && (
                    <div className="rounded-lg bg-muted/20 px-2 py-1.5 text-center">
                      <p className={`text-sm font-display font-bold capitalize ${
                        option.audit.dataConsistency === "high" ? "text-score-high" : "text-score-mid"
                      }`}>{option.audit.dataConsistency}</p>
                      <p className="text-[9px] text-muted-foreground font-body">Consistency</p>
                    </div>
                  )}
                </div>
              )}

              {option.audit.eliminationReasons && option.audit.eliminationReasons.length > 0 && (
                <div>
                  <p className="text-[10px] font-medium text-foreground/90 font-body mb-1">Offers eliminated</p>
                  <ul className="space-y-0.5">
                    {option.audit.eliminationReasons.slice(0, 5).map((reason, i) => (
                      <li key={i} className="flex items-start gap-1.5 text-[10px] text-muted-foreground font-body">
                        <Ban className="w-2.5 h-2.5 text-score-low shrink-0 mt-0.5" />{reason}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <VerifiedOffersSection option={option} />

              {option.landedCost && (
                <div className="px-2.5 py-1.5 rounded-md bg-accent/5 border border-accent/15">
                  <p className="text-[10px] font-body font-medium text-foreground mb-0.5">Estimated landed cost</p>
                  <p className="text-[10px] text-muted-foreground font-body">{option.landedCost.breakdown}</p>
                </div>
              )}

              {isFree ? (
                <div className="px-2.5 py-2 rounded-md bg-muted/20 border border-border/30 text-center">
                  <p className="text-[10px] text-muted-foreground font-body mb-1">Full audit trail available on Pro</p>
                  <p className="text-[9px] text-muted-foreground/60 font-body">Why it won, why others lost, filter reasons, retrieval details</p>
                </div>
              ) : (
                <>
                  <div>
                    <p className="text-[10px] font-medium text-foreground/90 font-body mb-1">
                      {option.tier === "A" ? "Why this won" : "Tradeoff to consider"}
                    </p>
                    <ul className="space-y-0.5">
                      {option.audit.whyWon.map((r, i) => (
                        <li key={i} className="flex items-start gap-1.5 text-[10px] text-secondary-foreground font-body">
                          <ShieldCheck className="w-2.5 h-2.5 text-score-high shrink-0 mt-0.5" />{r}
                        </li>
                      ))}
                    </ul>
                  </div>
                  {option.audit.whyOthersLost && option.audit.whyOthersLost.length > 0 && (
                    <div>
                      <p className="text-[10px] font-medium text-foreground/90 font-body mb-1">
                        {option.tier === "A" ? "Why others lost" : "Tradeoff to consider"}
                      </p>
                      <ul className="space-y-0.5">
                        {option.audit.whyOthersLost.map((r, i) => (
                          <li key={i} className="flex items-start gap-1.5 text-[10px] text-secondary-foreground font-body">
                            <AlertTriangle className="w-2.5 h-2.5 text-score-mid shrink-0 mt-0.5" />{r}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {option.audit.retrievalSummary && (
                    <div className="px-2.5 py-1.5 rounded-md bg-primary/5 border border-primary/10">
                      <p className="text-[10px] text-muted-foreground font-body leading-relaxed">{option.audit.retrievalSummary}</p>
                    </div>
                  )}
                </>
              )}
              <div className="px-2.5 py-1.5 rounded-md bg-muted/20 border border-border/20">
                <p className="text-[10px] text-muted-foreground font-body leading-relaxed">{option.audit.scope}</p>
                <p className="text-[9px] text-muted-foreground/60 font-body mt-0.5">Last checked: {option.audit.lastChecked}</p>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

interface Props {
  option: DecisionOption;
  index: number;
  coverage: CoverageState;
  query?: string;
  isWinner?: boolean;
}

export default function DecisionCard({ option, index, coverage, query = "", isWinner = false }: Props) {
  const [buyFlowOpen, setBuyFlowOpen] = useState(false);
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [upgradeFeature, setUpgradeFeature] = useState("");
  const config = labelConfig[option.label];
  const Icon = config.icon;
  const conf = confidenceConfig[option.confidence];
  const regretLine = getRegretLine(option);
  const mem = loadMemory();
  const behavior = isWinner ? analyzeBehavior() : null;
  const priceNumeric = parseFloat(option.price.replace(/[^0-9.]/g, "")) || 0;
  const { plan } = usePlan();
  const { user } = useAuth();

  const isEstimated = option.verification === "ai-estimated";

  const handleAction = async (type: "click" | "save") => {
    if (type === "save" && !user) {
      toast({
        title: "Sign in to save",
        description: "Create an account to save decisions and organize your watchlist.",
      });
      return;
    }
    // Free plan: allow exactly 1 save, then gate
    if (type === "save" && plan === "free") {
      const savedCount = parseInt(localStorage.getItem("dos_free_save_count") || "0", 10);
      if (savedCount >= 1) {
        setUpgradeFeature("Save more decisions");
        setUpgradeOpen(true);
        return;
      }
    } else if (type === "save" && !canUseFeatureForPlan(plan, "save")) {
      setUpgradeFeature("Save decisions");
      setUpgradeOpen(true);
      return;
    }
    trackOutcome({ type, query, category: "", optionId: option.id, optionLabel: option.label });

    if (user && type === "save") {
      await saveToCloudWatchlistFromCard({
        userId: user.id,
        productName: option.productName,
        query,
        category: "",
        label: option.label,
        price: option.price,
        priceNumeric,
        deliveryTime: option.deliveryTime,
        confidence: option.confidence,
        coverage: coverage,
        type: "save",
        notifyPriceDrop: true,
      });
      logEvent({ userId: user.id, eventType: "item_saved", metadata: { product: option.productName, query } });
    }

    if (type === "save") {
      addToWatchlist({
        productName: option.productName, query, category: "", label: option.label,
        price: option.price, priceNumeric, deliveryTime: option.deliveryTime,
        confidence: option.confidence, coverage, type: "save",
      });
      trackSave(option.productName, query, user?.id);
      // Track free plan save count
      if (plan === "free") {
        const prev = parseInt(localStorage.getItem("dos_free_save_count") || "0", 10);
        localStorage.setItem("dos_free_save_count", String(prev + 1));
      }
      toast({
        title: "Saved to your watchlist",
        description: plan === "free" ? "1 free save used. Upgrade to track more." : "You can rerun this anytime from your Watchlist.",
      });
    }
  };

  const openBuyFlow = () => {
    captureIntent({ type: "view_sellers", productName: option.productName, category: "", priceRange: option.price, query });
    setBuyFlowOpen(true);
  };

  // Verified product highlight: subtle green border when this product is verified but siblings may not be
  const isVerifiedProduct = option.verification === "verified";

  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: index * 0.08, ease: "easeOut" }}
      className={`relative rounded-xl bg-gradient-to-br ${config.gradient} flex flex-col transition-colors duration-300 ${
        isWinner
          ? `border-2 ${isVerifiedProduct ? "border-score-high/40" : "border-primary/35"} p-4 sm:p-5 gap-2.5 ring-1 ring-primary/10`
          : `border ${isVerifiedProduct && !isEstimated ? "border-score-high/20" : "border-border/40"} p-2.5 sm:p-3.5 hover:border-border/60 gap-2 opacity-85`
      }`}
    >
      {/* Badge + confidence */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <div className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border font-medium font-body ${
            isWinner ? "text-[11px] " + config.badgeClass : "text-[10px] " + config.badgeClass
          }`}>
            <Icon className={isWinner ? "w-3 h-3" : "w-2.5 h-2.5"} />
            {isWinner ? "YOUR DECISION" : option.label}
          </div>
          {isWinner && behavior?.autoAdjusted && (
            <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[8px] font-body text-primary bg-primary/10 border border-primary/15">
              <Sparkles className="w-2 h-2" />Personalized
            </span>
          )}
        </div>
        <div className="flex items-center gap-1">
          <ShieldCheck className={`w-2.5 h-2.5 ${conf.className}`} />
          <span className={`text-[9px] font-body ${conf.className}`}>
            {option.confidence === "high" 
              ? `Checked ${option.audit.sourcesAnalyzed || "multiple"} sources` 
              : conf.text}
          </span>
        </div>
      </div>

      {/* WHY THIS WINS — prominently above verdict for winner */}
      {isWinner && option.audit.whyWon.length > 0 && (
        <div className="rounded-lg bg-score-high/5 border border-score-high/15 px-3 py-2.5">
          <p className="text-[10px] text-score-high font-body font-medium uppercase tracking-wider mb-1.5">Why this wins</p>
          <ul className="space-y-1">
            {option.audit.whyWon.slice(0, 3).map((reason, i) => (
              <li key={i} className="flex items-start gap-1.5 text-[11px] text-foreground font-body">
                <ShieldCheck className="w-3 h-3 text-score-high shrink-0 mt-0.5" />{reason}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Winner verdict banner */}
      {isWinner && (
        <div className="rounded-lg bg-primary/5 border border-primary/15 px-3 py-2.5">
          <p className="text-xs font-body font-medium text-foreground leading-relaxed">{option.verdict}</p>
        </div>
      )}

      {/* Name + price + delivery */}
      <div>
        <div className="flex items-center gap-2">
          <h3 className={`font-display font-semibold text-foreground leading-tight ${isWinner ? "text-base sm:text-lg" : "text-sm"}`}>
            {option.productName}
          </h3>
          <VerificationBadge verification={option.verification} />
        </div>
        <div className={`flex items-center gap-3 mt-1 font-body ${isWinner ? "text-sm" : "text-xs"}`}>
          <span className={`font-semibold ${isEstimated ? "text-muted-foreground" : "text-foreground"}`}>
            {isEstimated ? `~${option.price}` : option.price}
          </span>
          <span className="text-muted-foreground flex items-center gap-1">
            <Clock className="w-2.5 h-2.5" />{option.deliveryTime}
          </span>
        </div>
      </div>

      {/* Reasoning */}
      <p className={`text-secondary-foreground font-body leading-relaxed ${isWinner ? "text-xs" : "text-[11px]"}`}>
        {option.explanation}
      </p>

      {/* Winner: preference adjustment */}
      {isWinner && behavior?.adjustmentLabel && (
        <p className="text-[10px] text-primary/60 font-body">
          Based on your history: {behavior.adjustmentLabel}
        </p>
      )}
      {isWinner && !behavior?.adjustmentLabel && mem.preferredPriority && mem.queriesCount >= 2 && (
        <p className="text-[10px] text-primary/60 font-body">
          Tuned to your preferences: {mem.preferredPriority}-focused{mem.localPreference === "local" ? ", local routes" : ""}. {mem.queriesCount} decisions made.
        </p>
      )}

      {/* WHAT YOU LOSE */}
      {isWinner && (regretLine || option.tradeoff) && (
        <div className="rounded-lg bg-score-low/5 border border-score-low/15 px-3 py-2.5">
          <p className="text-[10px] text-score-low font-body font-medium uppercase tracking-wider mb-1">What you lose</p>
          <p className="text-[11px] text-muted-foreground font-body leading-relaxed">{option.tradeoff}</p>
          {regretLine && (
            <p className="text-[10px] text-muted-foreground/70 font-body mt-1">{regretLine}</p>
          )}
        </div>
      )}

      {/* Alternative tradeoff */}
      {!isWinner && (
        <div className="flex items-start gap-2 px-2.5 py-1.5 rounded-md bg-muted/25 border border-border/25">
          <AlertTriangle className="w-2.5 h-2.5 text-score-mid shrink-0 mt-0.5" />
          <p className="text-[10px] text-muted-foreground font-body leading-relaxed">{option.tradeoff}</p>
        </div>
      )}

      {/* Scores — visually soften for estimated */}
      <div className={`flex flex-col ${isWinner ? "gap-1.5" : "gap-1"}`}>
        {isEstimated && (
          <p className="text-[9px] text-muted-foreground/50 font-body italic">Estimated scores. No verified retailer data found.</p>
        )}
        <ScoreBar label="Price" value={option.scores.price} isEstimated={isEstimated} />
        <ScoreBar label="Delivery" value={option.scores.delivery} isEstimated={isEstimated} />
        <ScoreBar label="Trust" value={option.scores.trust} isEstimated={isEstimated} />
        <ScoreBar label="Quality" value={option.scores.quality} isEstimated={isEstimated} />
      </div>

      {/* Audit — winner inline, alternatives expand-only */}
      {isWinner && <AuditSection option={option} plan={plan} />}

      {/* Buy button */}
      <button
        onClick={openBuyFlow}
        className={`w-full flex items-center justify-center gap-2 rounded-lg font-body font-medium transition-all ${
          isWinner
            ? "py-3 text-sm bg-primary text-primary-foreground hover:opacity-90"
            : "py-2 text-xs border border-border/50 bg-secondary text-secondary-foreground hover:border-primary/25 hover:text-foreground"
        }`}
      >
        <ShoppingCart className={isWinner ? "w-4 h-4" : "w-3 h-3"} />
        {isWinner ? "Best place to buy" : "View options"}
      </button>

      {/* Save + Reorder actions */}
      <div className="flex items-center gap-1">
        <button onClick={() => handleAction("save")} className="flex-1 flex items-center justify-center gap-1 py-1.5 rounded-md text-[10px] font-body text-muted-foreground hover:text-foreground hover:bg-muted/25 transition-colors">
          <Bookmark className="w-2.5 h-2.5" />Track this decision
        </button>
        {(() => {
          const resolved = resolveCategory(option.productName);
          const cycle = resolved ? getRepeatBuyCycle(resolved.productType) : null;
          if (!cycle || !user) return null;
          return (
            <button
              onClick={async () => {
                await saveReorderItem({
                  userId: user.id,
                  productName: option.productName,
                  category: resolved!.productType,
                  cycleDays: cycle.cycleDays,
                  lastPrice: option.price,
                });
                toast({ title: "Reorder tracked", description: `We'll remind you in ${cycle.cycleDays} days.` });
              }}
              className="flex items-center justify-center gap-1 px-2 py-1.5 rounded-md text-[10px] font-body text-accent hover:text-foreground hover:bg-accent/10 transition-colors"
            >
              <Bookmark className="w-2.5 h-2.5" />Reorder
            </button>
          );
        })()}
      </div>

      {/* Purchase feedback — appears after 5s for winner */}
      {isWinner && <PurchaseFeedback productName={option.productName} query={query} />}

      <BuyFlowModal option={option} coverage={coverage} query={query} open={buyFlowOpen} onClose={() => setBuyFlowOpen(false)} />
      <UpgradeModal open={upgradeOpen} onClose={() => setUpgradeOpen(false)} requiredFeature={upgradeFeature} />
    </motion.div>
  );
}
